import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { CalendarClock, ArrowRight, X } from 'lucide-react';
import { useAppStore } from '../../store';
import { useTasksByStatus, useTrackTasks, moveToTrack } from '../../hooks/useTasks';
import { queryClient } from '../../api/queryClient';
import type { Task } from '../../types';
import { format, subDays } from 'date-fns';

interface DailyPlanningPanelProps {
  onClose: () => void;
}

export function DailyPlanningPanel({ onClose }: DailyPlanningPanelProps) {
  const selectedDate = useAppStore((s) => s.selectedDate);
  const spaceId = useAppStore((s) => s.currentSpaceId);
  const yesterday = format(subDays(new Date(selectedDate + 'T00:00:00'), 1), 'yyyy-MM-dd');

  const yesterdayTasks = useTrackTasks(yesterday);
  const backlogTasks = useTasksByStatus('backlog');

  const incompleteTasks = useMemo(
    () => yesterdayTasks.filter((t) => t.trackProgress < 100 && !t.parentId),
    [yesterdayTasks]
  );

  const moveToToday = async (task: Task) => {
    await moveToTrack(task.id, selectedDate);
    queryClient.invalidateQueries({ queryKey: ['tasks', spaceId, 'backlog'] });
    queryClient.invalidateQueries({ queryKey: ['tasks', spaceId, 'track'] });
  };

  const topBacklog = backlogTasks.filter((t) => !t.parentId).slice(0, 5);

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.2 }}
      className="surface-raised p-4 shrink-0"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <CalendarClock size={16} className="text-accent" />
          <h3 className="text-caption font-semibold text-text-primary">Plan Your Day</h3>
        </div>
        <button onClick={onClose} aria-label="Close planning panel" className="text-text-muted hover:text-text-secondary p-2 rounded-lg interactive-ghost">
          <X size={16} />
        </button>
      </div>

      {incompleteTasks.length > 0 && (
        <div className="mb-4">
          <p className="text-label text-text-muted mb-3">
            Yesterday's unfinished ({incompleteTasks.length})
          </p>
          <div className="space-y-1.5">
            {incompleteTasks.map((task) => (
              <div key={task.id} className="surface-card flex items-center justify-between px-3 py-2.5">
                <div className="flex items-center gap-2.5">
                  <div className={`w-2 h-2 rounded-full ${
                    task.priority === 'p1' ? 'bg-p1' : task.priority === 'p2' ? 'bg-p2' : 'bg-p3'
                  }`} />
                  <span className="text-caption text-text-primary">{task.title}</span>
                  <span className="text-micro text-text-muted">{task.trackProgress}%</span>
                </div>
                <button onClick={() => moveToToday(task)} className="flex items-center gap-1.5 text-micro text-accent hover:text-accent-hover font-semibold">
                  Today <ArrowRight size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {topBacklog.length > 0 && (
        <div>
          <p className="text-label text-text-muted mb-3">From backlog</p>
          <div className="space-y-1.5">
            {topBacklog.map((task) => (
              <div key={task.id} className="surface-card flex items-center justify-between px-3 py-2.5">
                <div className="flex items-center gap-2.5">
                  <div className={`w-2 h-2 rounded-full ${
                    task.priority === 'p1' ? 'bg-p1' : task.priority === 'p2' ? 'bg-p2' : 'bg-p3'
                  }`} />
                  <span className="text-caption text-text-primary">{task.title}</span>
                </div>
                <button onClick={() => moveToToday(task)} className="flex items-center gap-1.5 text-micro text-accent hover:text-accent-hover font-semibold">
                  Today <ArrowRight size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {incompleteTasks.length === 0 && topBacklog.length === 0 && (
        <p className="text-caption text-text-muted text-center py-4">Nothing to pick from. Add tasks to your backlog first.</p>
      )}
    </motion.div>
  );
}
